import { API_BASE_URL } from '../config/api';

// Debug: Log the actual API URL being used
console.log('🔍 UsageService API_BASE_URL:', API_BASE_URL);

export interface UsageData {
  brandId: string;
  featureCode: string;
  featureName: string;
  currentMonth: string;
  usageCount: number;
  baseQuota: number;
  remaining: number;
  overageCount: number;
  customHardLimit?: number;
  pricing?: {
    basePrice: number;
    overagePrice: number;
    overageUnit: number;
    currency: string;
  };
}

export interface UsageHistoryData {
  month: string;
  featureCode: string;
  featureName: string;
  usageCount: number;
  baseQuota: number;
  overageCount: number;
  basePrice: number;
  overagePrice: number;
  totalPrice: number;
  currency: string;
}

export interface UsageWarning {
  featureCode: string;
  featureName: string;
  usageCount: number;
  baseQuota: number;
  overageCount: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export class UsageService {
  private static getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  private static getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Get usage statistics for all features
   */
  public static async getUsageStats(brandId: string): Promise<UsageData[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/usage/brands/${brandId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.features;
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      throw error;
    }
  }

  /**
   * Get usage for a specific feature
   */
  public static async getFeatureUsage(brandId: string, featureCode: string): Promise<UsageData> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/usage/brands/${brandId}/features/${featureCode}`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching feature usage:', error);
      throw error;
    }
  }

  /**
   * Get usage history
   */
  public static async getUsageHistory(
    brandId: string,
    months: number = 6,
    featureCode?: string
  ): Promise<UsageHistoryData[]> {
    try {
      const params = new URLSearchParams({
        months: months.toString(),
        ...(featureCode && { featureCode }),
      });

      const response = await fetch(
        `${API_BASE_URL}/usage/brands/${brandId}/history?${params}`,
        {
          method: 'GET',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.history;
    } catch (error) {
      console.error('Error fetching usage history:', error);
      throw error;
    }
  }

  /**
   * Set custom hard limit
   */
  public static async setCustomLimit(
    brandId: string,
    featureCode: string,
    limit: number
  ): Promise<UsageData> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/usage/brands/${brandId}/features/${featureCode}/limit`,
        {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({ limit }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error setting custom limit:', error);
      throw error;
    }
  }

  /**
   * Remove custom hard limit
   */
  public static async removeCustomLimit(
    brandId: string,
    featureCode: string
  ): Promise<UsageData> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/usage/brands/${brandId}/features/${featureCode}/limit`,
        {
          method: 'DELETE',
          headers: this.getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error removing custom limit:', error);
      throw error;
    }
  }

  /**
   * Get usage warnings
   */
  public static async getWarnings(brandId: string): Promise<UsageWarning[]> {
    try {
      const url = `${API_BASE_URL}/usage/brands/${brandId}/warnings`;
      console.log('🔍 UsageService.getWarnings - Full URL:', url);
      console.log('🔍 UsageService.getWarnings - API_BASE_URL:', API_BASE_URL);
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data.warnings;
    } catch (error) {
      console.error('Error fetching usage warnings:', error);
      console.error('Failed URL was:', `${API_BASE_URL}/usage/brands/${brandId}/warnings`);
      return []; // Return empty array on error to prevent UI breaks
    }
  }

  /**
   * Export usage report (CSV format)
   */
  public static async exportUsageReport(brandId: string, month: string): Promise<Blob> {
    try {
      // Get usage history for the month
      const history = await this.getUsageHistory(brandId, 1);
      
      // Convert to CSV
      const csv = this.convertToCSV(history);
      
      return new Blob([csv], { type: 'text/csv' });
    } catch (error) {
      console.error('Error exporting usage report:', error);
      throw error;
    }
  }

  /**
   * Helper: Convert data to CSV
   */
  private static convertToCSV(data: UsageHistoryData[]): string {
    if (data.length === 0) return '';

    const headers = [
      'Month',
      'Feature',
      'Usage Count',
      'Base Quota',
      'Overage',
      'Base Price',
      'Overage Price',
      'Total Price',
      'Currency',
    ];

    const rows = data.map((row) => [
      row.month,
      row.featureName,
      row.usageCount,
      row.baseQuota,
      row.overageCount,
      row.basePrice,
      row.overagePrice,
      row.totalPrice,
      row.currency,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    return csvContent;
  }
}

